import React, { useEffect, useRef } from "react";

/**
 * CalendlyInline — embed inline du widget Calendly officiel.
 *
 * Robustesse :
 *  1. Le script est chargé UNE SEULE FOIS (cache : window.__calendlyScriptLoaded).
 *  2. Quand le composant se monte, on attend que window.Calendly soit dispo
 *     puis on appelle Calendly.initInlineWidget() sur notre <div> personnel.
 *     Cela évite que le widget reste vide lors d'une navigation SPA (React Router).
 *  3. Hauteur minimale 700px pour afficher le calendrier directement.
 */
// Génère dynamiquement le mois courant (YYYY-MM) pour que Calendly ouvre toujours sur le mois actuel
function getCurrentMonthParam() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${yyyy}-${mm}`;
}

const DEFAULT_URL = `https://calendly.com/nassim15000/audit-offert?month=${getCurrentMonthParam()}&hide_event_type_details=0&hide_gdpr_banner=1&primary_color=111111&background_color=ffffff`;

function ensureScriptLoaded() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Calendly && typeof window.Calendly.initInlineWidget === "function") {
      return resolve(true);
    }
    const existing = document.querySelector(
      'script[src*="calendly.com/assets/external/widget.js"]'
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(true), { once: true });
      // Si déjà chargé (load déjà passé), interroger window.Calendly via polling court
      let tries = 0;
      const id = setInterval(() => {
        if (window.Calendly) {
          clearInterval(id);
          resolve(true);
        } else if (tries++ > 40) {
          clearInterval(id);
          resolve(false);
        }
      }, 100);
      return;
    }
    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function CalendlyInline({
  url = DEFAULT_URL,
  minHeight = 720,
  testId = "calendly-inline-widget",
  className = "",
}) {
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    ensureScriptLoaded().then((ok) => {
      if (cancelled) return;
      if (!ref.current) return;
      if (ok && window.Calendly && typeof window.Calendly.initInlineWidget === "function") {
        // Nettoie le conteneur (en cas de remount) avant init
        ref.current.innerHTML = "";
        window.Calendly.initInlineWidget({
          url,
          parentElement: ref.current,
          prefill: {},
          utm: {},
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <div
      ref={ref}
      data-testid={testId}
      data-calendly-url={url}
      className={`w-full ${className}`}
      style={{ minWidth: "320px", height: `${minHeight}px`, minHeight: `${minHeight}px` }}
    />
  );
}
